import type { Targeting } from '@orderlymeds/measurements-schemas';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useExclusionGroups } from '../../api/exclusion-groups';
import { useCreateExperiment } from '../../api/experiments';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Input } from '../../components/Input';
import { PageHeader } from '../../components/PageHeader';
import { Select } from '../../components/Select';
import { TargetingBuilder } from '../../components/TargetingBuilder';
import type { Variant } from '../../components/VariantWeightEditor';
import { nextVariantKey, VariantWeightEditor } from '../../components/VariantWeightEditor';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import shared from '../shared.module.css';
import styles from './ExperimentCreatePage.module.css';

const DEFAULT_VARIANTS: Variant[] = [
	{ key: nextVariantKey(), id: 'control', name: 'Control', weight: 5000 },
	{ key: nextVariantKey(), id: 'treatment', name: 'Treatment', weight: 5000 },
];

export function ExperimentCreatePage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const createExperiment = useCreateExperiment(id ?? '');
	const { data: exclusionGroups } = useExclusionGroups(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Experiments', 'New');

	const [name, setName] = useState('');
	const [description, setDescription] = useState('');
	const [variants, setVariants] = useState<Variant[]>(DEFAULT_VARIANTS);
	const [targeting, setTargeting] = useState<Targeting | null>(null);
	const [exclusionGroupId, setExclusionGroupId] = useState('');
	const [mode, setMode] = useState<'ab' | 'bandit'>('ab');
	const [metric, setMetric] = useState('');

	const weightTotal = variants.reduce((sum, v) => sum + v.weight, 0);
	const weightsValid = weightTotal === 10000;

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!weightsValid) return;

		const result = await createExperiment.mutateAsync({
			name: name.trim(),
			description: description.trim() || undefined,
			variants: variants.map((v) => ({
				id: v.id.trim(),
				name: v.name.trim(),
				weight: v.weight,
			})),
			targeting: targeting ?? undefined,
			exclusion_group_id: exclusionGroupId || undefined,
			mode,
			metric: mode === 'bandit' && metric.trim() ? metric.trim() : undefined,
		});

		navigate(`/projects/${id}/experiments/${result.id}`);
	}

	return (
		<div>
			<PageHeader title="New Experiment" breadcrumbs={breadcrumbs} />

			<div className={shared.content}>
				<Card>
					<form onSubmit={handleSubmit} className={shared.form}>
						<Input
							label="Name"
							name="exp-name"
							value={name}
							onChange={setName}
							required
							placeholder="Hero Copy Test"
						/>
						<Input
							label="Description"
							name="exp-description"
							value={description}
							onChange={setDescription}
							placeholder="Optional description of this experiment"
						/>

						<Select
							label="Mode"
							name="exp-mode"
							value={mode}
							onChange={(v) => setMode(v as 'ab' | 'bandit')}
							options={[
								{ value: 'ab', label: 'A/B Test' },
								{ value: 'bandit', label: 'Bandit' },
							]}
						/>

						{mode === 'bandit' && (
							<div>
								<Input
									label="Metric"
									name="exp-metric"
									value={metric}
									onChange={setMetric}
									placeholder="e.g., purchase_completed"
									required
								/>
								<p className={styles.fieldHint}>Custom event name that counts as a conversion</p>
							</div>
						)}

						<Select
							label="Exclusion Group"
							name="exp-exclusion-group"
							value={exclusionGroupId}
							onChange={setExclusionGroupId}
							placeholder="None (no exclusion group)"
							options={(exclusionGroups ?? []).map((g) => ({
								value: g.id,
								label: g.name,
							}))}
						/>

						<div className={styles.section}>
							<h3 className={shared.sectionTitle}>
								{mode === 'bandit'
									? 'Variants (initial weights — updated automatically by Thompson Sampling)'
									: 'Variants'}
							</h3>
							<VariantWeightEditor variants={variants} onChange={setVariants} minVariants={2} />
						</div>

						<div className={styles.section}>
							<TargetingBuilder value={targeting} onChange={setTargeting} />
						</div>

						{createExperiment.error && (
							<p className={shared.formError}>
								{createExperiment.error instanceof Error
									? createExperiment.error.message
									: 'Failed to create experiment'}
							</p>
						)}

						<div className={shared.formActions}>
							<Button
								variant="secondary"
								type="button"
								onClick={() => navigate(`/projects/${id}/experiments`)}
							>
								Cancel
							</Button>
							<Button
								variant="primary"
								type="submit"
								loading={createExperiment.isPending}
								disabled={!name.trim() || !weightsValid}
							>
								Create Experiment
							</Button>
						</div>
					</form>
				</Card>
			</div>
		</div>
	);
}
